import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type OtherFormType, getOtherFormSchema } from "Forms/Schemas/GoalsFulfillment/ProposedSolution/otherFormSchema";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListContractProposalsQueryKey,
  useCreateContractProposal,
  usePatchContractProposal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  ContractProposalBaseInvestmentToolType,
  CreateContractProposalOtherBodyInvestmentToolType,
  PatchContractProposalOtherBodyInvestmentToolType,
} from "Infrastructure/ApiZfp/Schema";
import type {
  ContractProposalBaseSolutionHorizonType,
  CreateContractProposalOtherBody,
  PatchContractProposalOtherBody,
} from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailOther } from "Infrastructure/ApiZfp/Schema/contractProposalDetailOther";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";

type Props = {
  readonly edit?: boolean;
  readonly partner?: string;
  readonly proposedSolutionId?: string;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly initialData?: ContractProposalDetailOther | null;
};

export const OtherForm = forwardRef<{}, Props>(({ partner, edit, proposedSolutionId, horizonType, onSuccess, initialData }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const partnerRef = useRef(partner);
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { assetAllocationOverview } = useGoalsFulfillmentContext();

  const { mutate: createMutation } = useCreateContractProposal();
  const { mutate: updateMutation } = usePatchContractProposal();

  const methods = useForm<OtherFormType>({
    resolver: yupResolver(getOtherFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
    mode: "onChange",
    defaultValues:
      edit && initialData
        ? {
            investmentToolType: initialData.investmentToolType,
            partner: partner ?? "",
            oneTimeDeposit: initialData.oneTimeAmount?.value ?? 0,
            regularAmount: initialData.regularAmount?.value ?? 0,
            horizon: initialData.currentSolutionHorizon ?? 0,
            appreciationRate: initialData.appreciationRate ?? 0,
            entryFee: initialData.entryFee ?? 0,
            oneTimeFee: initialData.oneTimeFee?.value ?? 0,
            managementFee: initialData.managementFee ?? 0,
            exitFee: initialData.exitFee ?? 0,
            equities: initialData.assetAllocation?.equities ?? 0,
            bonds: initialData.assetAllocation?.bonds ?? 0,
            commodities: initialData.assetAllocation?.commodities ?? 0,
            realEstate: initialData.assetAllocation?.realEstate ?? 0,
            cash: initialData.assetAllocation?.cash ?? 0,
            other: initialData.assetAllocation?.others ?? 0,
            note: initialData.note ?? "",
          }
        : {
            investmentToolType: ContractProposalBaseInvestmentToolType.other,
            partner: partner ?? "",
          },
  });

  const { control, handleSubmit, reset, trigger } = methods;

  useEffect(() => {
    if (edit && initialData) {
      reset({
        investmentToolType: initialData.investmentToolType,
        partner: partnerRef.current ?? "",
        oneTimeDeposit: initialData.oneTimeAmount?.value ?? 0,
        regularAmount: initialData.regularAmount?.value ?? 0,
        horizon: initialData.currentSolutionHorizon ?? 0,
        appreciationRate: initialData.appreciationRate ?? 0,
        entryFee: initialData.entryFee ?? 0,
        oneTimeFee: initialData.oneTimeFee?.value ?? 0,
        managementFee: initialData.managementFee ?? 0,
        exitFee: initialData.exitFee ?? 0,
        equities: initialData.assetAllocation?.equities ?? 0,
        bonds: initialData.assetAllocation?.bonds ?? 0,
        commodities: initialData.assetAllocation?.commodities ?? 0,
        realEstate: initialData.assetAllocation?.realEstate ?? 0,
        cash: initialData.assetAllocation?.cash ?? 0,
        other: initialData.assetAllocation?.others ?? 0,
        note: initialData.note ?? "",
      });
    }
  }, [edit, initialData, reset]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (edit) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
    triggerValidation: () => {
      trigger();
    },
  }));

  const onCreate = (data: OtherFormType) => {
    if (!horizonType) return;

    const payload: CreateContractProposalOtherBody = {
      investmentToolType: CreateContractProposalOtherBodyInvestmentToolType.other,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      managementFee: data.managementFee ?? 0,
      entryFee: data.entryFee ?? 0,
      exitFee: data.exitFee ?? 0,
      note: data.note ?? "",
      oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      oneTimeFee: { value: data.oneTimeFee ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
    };

    createMutation(
      { analysisId, versionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  const onEdit = (data: OtherFormType) => {
    if (!proposedSolutionId) return;

    const payload: PatchContractProposalOtherBody = {
      investmentToolType: PatchContractProposalOtherBodyInvestmentToolType.other,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      managementFee: data.managementFee ?? 0,
      entryFee: data.entryFee ?? 0,
      exitFee: data.exitFee ?? 0,
      note: data.note ?? "",
      oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      oneTimeFee: { value: data.oneTimeFee ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
    };

    updateMutation(
      { analysisId, versionId, contractProposalGuid: proposedSolutionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="oneTimeDeposit"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeDeposit)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="regularAmount"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="horizon"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                  required
                  scale={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.Years)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="appreciationRate"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit="%"
                  fullWidth
                />
              </Grid>

              <Stack direction="column">
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" color="secondary" fontWeight={500}>
                    {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Fees)}
                  </Typography>
                </Box>

                <Grid container spacing={Spacing.XL}>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="entryFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="oneTimeFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="managementFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="exitFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ExitFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          </Box>

          <Box>
            <CharacteristicsAccordion<OtherFormType>
              key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
              defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
            />
          </Box>

          <Divider />

          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                  maxLength={NOTE_MAX_LENGTH}
                  placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
